import { useEffect, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { usePermissions } from '@/modules/common';
import { getOrganization } from '@/modules/common/api';
import { OrganizationSettingsTemplate } from '@/modules/common/components/templates';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { SettingsPageWrapper } from '@/modules/settings';

import { DeleteOrganizationCard } from '../../components/organization/delete-organization-card';
import { SettingsOutletContext } from '../../types/settings-footer';

export const OrgSettingsPage = () => {
  const { t } = useTranslation(['common', 'authenticated.organization']);
  const { setFooterState } = useOutletContext<SettingsOutletContext>();
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const formResetRef = useRef<(() => void) | null>(null);
  const formSubmitRef = useRef<(() => void) | null>(null);

  const { hasPermission } = usePermissions();
  const hasFullAccess = hasPermission(OpsiumPolicy.FullAccess);

  const {
    data: _organizationData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organization-summary'],
    queryFn: getOrganization,
  });

  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty,
    onDiscard: () => {
      if (formResetRef.current) {
        formResetRef.current();
      }
      setIsDirty(false);
    },
  });

  // Update footer state when form state changes
  useEffect(() => {
    setFooterState({
      isVisible: true,
      isEnabled: isDirty,
      isLoading: isSaving,
      onSave: () => {
        if (formSubmitRef.current) {
          formSubmitRef.current();
        }
      },
    });
  }, [isDirty, isSaving, setFooterState]);

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error ?? null}
      unsavedChangesGuard={{
        isModalOpen: unsavedChangesGuard.isModalOpen,
        onDiscard: unsavedChangesGuard.handleModalDiscard,
        onCancel: unsavedChangesGuard.handleModalCancel,
      }}
    >
      <div className="space-y-6">
        <OrganizationSettingsTemplate
          submitButtonText={t('common:buttons.save')}
          wrapInCard={true}
          title={t('authenticated.organization:general')}
          onFormStateChange={(isDirty, reset) => {
            setIsDirty(isDirty);
            formResetRef.current = reset;
          }}
          onSavingStateChange={setIsSaving}
          onSubmitRefChange={submit => {
            formSubmitRef.current = submit;
          }}
        />

        {hasFullAccess && <DeleteOrganizationCard />}
      </div>
    </SettingsPageWrapper>
  );
};
